/**
 * MONSTER TOWN — THE KITCHEN. COOKING CREW (src/cooking/**).
 *
 * Three screens, no words, nothing that can go wrong:
 *
 *   PICK   every recipe as a big picture of the dish, with the things it needs
 *          underneath as pictures and countable dots. A recipe you cannot make
 *          yet is still on the shelf and still tappable — it just shows you what
 *          is missing and where that comes from.
 *   COOK   the recipe card on the wall (ingredients IN ORDER, an arrow, and the
 *          dish it makes in full colour), THE POT standing on the counter, and
 *          everything in the bag laid out on the counter in front of it. Tap the
 *          right thing and it drops into the pot and STAYS THERE, in the same
 *          picture you tapped. Tap a wrong thing and the kitchen GIGGLES, the pot
 *          shrugs, and nothing at all is consumed or lost (brief §5: "a wrong
 *          ingredient just gets a giggle and can be swapped out"). Then the pot
 *          itself is the stir: three or four big taps, purely because it feels
 *          good.
 *   DONE   the dish, huge, with two equally big buttons — eat it or keep it.
 *          Both are a yes; there is no "throw it away".
 *
 * SIX THINGS THIS SCREEN LEARNED THE HARD WAY, from a critic reading the PNGs:
 *
 *  1. THE BIGGEST, BRIGHTEST THING MUST BE THE ANSWER. It used to be a 34 %
 *     opacity ghost of the finished dish, which is not a control, gives no
 *     feedback, and next to this game's chunky saturated art reads as *disabled*.
 *     A three-year-old's first tap goes to the biggest brightest thing, so that
 *     is now the pot: it always reacts, it stirs the moment stirring is what to
 *     do, and until then it wobbles and makes the thing to tap jump on the
 *     counter. The dish being worked towards is on the recipe card in FULL
 *     COLOUR, where it is a promise rather than a greyed-out control.
 *  2. MID-COOK MUST NOT LOOK LIKE NOT-STARTED. What is in the pot is drawn in
 *     the pot, in the same picture the kid tapped in the tray, and the pot
 *     steams the moment anything is in it. One fruit in is visibly one fruit in.
 *  3. ONE WAY OUT, NOT TWO. The 112 px back button `panelShell()` builds in the
 *     corner is the way out of every panel in this game. This sheet used to add
 *     a SECOND one, same glyph, bottom-centre — the largest, easiest slot on the
 *     screen spent on a duplicate of the control directly above it. The foot now
 *     only ever holds a real CHOICE (eat it / keep it), never a second back.
 *  4. TWO KINDS OF COUNTING NEED TWO SHAPES. Progress ("how many are in", "how
 *     many stirs to go") is round HOLES that fill green. Quantity ("how many I
 *     have") is small ink TICKS on a chip. Same-shaped dots in two colours
 *     meaning two different things is a reading test, which is exactly what
 *     LAW #4 forbids.
 *  5. IT HAS TO LOOK LIKE A KITCHEN. Half the screen was flat cream. Every view
 *     now stands in a drawn room — tiled wall, window with the day outside, a
 *     shelf of jars, a pan on its rail (`kitchenRoomSvg`, decoration only,
 *     `pointer-events: none`, outlined soft so the controls stay the nearest,
 *     darkest shapes on the glass).
 *  6. OPENING ANNOUNCES ITSELF. `openCookSheet()` used only to LISTEN on
 *     `ui:panel`. Opened directly — which the interiors crew's cooker does on
 *     every third tap — nothing else on the bus knew, so the HUD chrome stayed
 *     drawn over the sheet and the zoo sheet / collection book could sit open
 *     underneath it. It now emits the same event it listens for, guarded against
 *     hearing itself.
 *
 * WHY THIS IS NOT BUILT ON THE HUD'S `PanelHost`: that host is constructed
 * inside `src/ui/hud.ts` and never exported, and `src/ui/**` belongs to another
 * crew. This layer is our own, sits at the same z-index, and is driven by the
 * SAME `ui:panel` event the HUD uses — so opening the shop closes the kitchen
 * and vice versa, with no shared code and no edit to their files. The chrome
 * itself is `panelShell()`, which is a pure function, so the 112 px back button
 * and the ≥88 px targets come from exactly the same place as everywhere else.
 */

import './cooking.css'
import { bus } from '../core/events'
import { app } from '../app/context'
import { wallet } from '../core/economy'
import { inventory } from '../core/inventory'
import { collection } from '../core/collection'
import { needs } from '../core/schedule'
import { save } from '../core/save'
// `dots` from the HUD crew is deliberately NOT imported here any more: this
// screen counts two different things and the shared dot can only say one of
// them (§4 above). `holes()` and `ticks()` below are this crew's own, in this
// crew's stylesheet, and nothing outside `src/cooking/**` changes shape.
// (An earlier round left this import list half-migrated and the repo red; the
// errands fix crew repaired it so everyone could compile. Thank you.)
import { button, clear, el, onTap } from '../ui/dom'
import { iconEl } from '../ui/icons'
import { panelShell } from '../ui/panels'
import {
  arrowBody,
  kitchenRoomSvg,
  picEl,
  potBackSvg,
  potFrontSvg,
  sourceBody,
  spoonSvg,
  steamSvg,
  svgEl,
} from './pics'
import {
  allRecipes,
  getIngredient,
  lookOf,
  recipeNeeds,
  sourcePicOf,
  type RecipeDef,
} from './types'

export const COOK_PANEL = 'cook'

type View = 'pick' | 'cook' | 'done'

/**
 * What just happened, for ONE repaint. `repaint()` rebuilds the whole sheet, so
 * a CSS animation keyed off a data attribute plays exactly once and cannot get
 * stuck on — which is why this is a flag consumed by the paint rather than a
 * timer somebody has to remember to clear.
 */
type Pulse = 'fill' | 'stir' | 'point' | 'giggle' | 'source'

interface CookState {
  view: View
  recipe: RecipeDef | null
  /** How many of each STEP (by index) are in the pot. Same length as steps. */
  filled: number[]
  /** Stirs done so far. */
  stirs: number
  /** The dish just made, for the DONE screen. */
  made: RecipeDef | null
  /** Consumed by the next paint. Never part of the QA surface or the save. */
  pulse: Pulse | null
  /** Which tray tile the finger actually landed on, for `giggle` / `source`. */
  pulseItem: string | null
}

const EMPTY: CookState = {
  view: 'pick',
  recipe: null,
  filled: [],
  stirs: 0,
  made: null,
  pulse: null,
  pulseItem: null,
}

let layer: HTMLElement | null = null
let state: CookState = { ...EMPTY }

export function cookSheetOpen(): boolean {
  return layer !== null && layer.childElementCount > 0
}

/** QA surface — what the kitchen is doing right now. Never shown to a kid. */
export function cookSheetState(): {
  open: boolean
  view: View
  recipe: string | null
  filled: number[]
  stirs: number
} {
  return {
    open: cookSheetOpen(),
    view: state.view,
    recipe: state.recipe?.id ?? null,
    filled: [...state.filled],
    stirs: state.stirs,
  }
}

// ---------------------------------------------------------------------------
// pieces
// ---------------------------------------------------------------------------

/** A food picture at a given size. Ingredient or dish — the caller never knows. */
function foodEl(itemId: string, size: number): HTMLSpanElement {
  const l = lookOf(itemId)
  return picEl(l.pic, { color: l.color, accent: l.accent }, size)
}

/**
 * SLOTS TO FILL — the "how far along am I" shape. Round sockets that fill with
 * a green disc, one per thing wanted. Used for what is in the pot, what a
 * recipe still needs, and how many stirs are left: everywhere the answer is a
 * progress, the picture is a hole waiting to be filled.
 */
function holes(total: number, filled: number): HTMLElement {
  const row = el('div', 'mt-cook-holes')
  const n = Math.min(Math.max(1, Math.round(total)), 8)
  for (let i = 0; i < n; i++) {
    const h = el('span', 'mt-cook-hole')
    h.dataset['in'] = String(i < filled)
    row.append(h)
  }
  return row
}

/**
 * HOW MANY I HAVE — the "what is in my bag" shape. Ink tally marks on a little
 * chip. Deliberately not round and never green: a hole is something to fill, a
 * tick is something you already own, and telling those two apart must not
 * depend on noticing a colour (§4).
 */
function ticks(count: number): HTMLElement {
  const chip = el('div', 'mt-cook-ticks')
  const n = Math.min(Math.max(0, Math.round(count)), 8)
  chip.dataset['none'] = String(n === 0)
  for (let i = 0; i < n; i++) chip.append(el('span', 'mt-cook-tick'))
  return chip
}

/**
 * WHERE THIS COMES FROM, as a picture of the place. Always drawn, never only
 * after a tap — see the header of `sourceCard()` for why that matters.
 */
function sourceBadge(itemId: string, size: number): HTMLElement {
  const s = el('span', 'mt-cook-src')
  s.append(svgEl(sourceBody(sourcePicOf(itemId)), size, 'mt-ico mt-cook-srcpic'))
  return s
}

/** A recipe's shopping line: the picture, and the slots your bag can fill. */
function haveRow(itemId: string, want: number): HTMLElement {
  const row = el('div', 'mt-cook-have')
  const short = inventory.count(itemId) < want
  row.append(foodEl(itemId, 46))
  row.append(holes(want, Math.min(want, inventory.count(itemId))))
  // A short line on the SHELF says where to go too, so a kid browsing recipes
  // with an empty bag is never looking at a card that only tells them "no".
  if (short) row.append(sourceBadge(itemId, 34))
  row.dataset['short'] = String(short)
  return row
}

/** Raw SVG into a span, with the whitespace BETWEEN tags collapsed — it is real
 *  text in the DOM and the room alone would add a hundred nodes of it. (It does
 *  not get `.mt-cook-layer` to a genuinely empty `textContent`: the HUD crew's
 *  `iconEl` injects its markup the same way, in every panel in the game.) */
function svgSpan(className: string, markup: string): HTMLSpanElement {
  const s = el('span', className)
  s.innerHTML = markup.replace(/>\s+</g, '><').trim()
  return s
}

/**
 * THE ROOM BEHIND EVERYTHING. Decoration: `pointer-events: none`, one static
 * SVG, no animation, drawn under every view so the kitchen looks like a kitchen
 * and not like a settings screen (§5).
 */
function roomEl(): HTMLElement {
  const room = el('div', 'mt-cook-room')
  room.append(svgSpan('mt-cook-roomart', kitchenRoomSvg()))
  return room
}

/** The stage every view stands on: the room, then the view's own things on top. */
function sceneEl(extraClass = ''): HTMLElement {
  const scene = el('div', `mt-cook-scene ${extraClass}`.trim())
  scene.append(roomEl())
  return scene
}

/**
 * ===========================================================================
 * "I HAVEN'T GOT ANY" IS A PICTURE ON THE SCREEN, NOT AN EVENT THAT FADES
 * ===========================================================================
 * ROUND-2 DEFECT, and it is worth writing down because the shape of the bug is
 * the interesting part. Tapping an ingredient you have none of DID react: the
 * tile wobbled and a 36 px badge was pinned to its corner. But
 *
 *   - the tile it decorated was drawn at 50 % opacity, which reads as disabled,
 *   - the badge was a generic HUD line icon, not a picture of anywhere, and
 *   - `addToPot` then ran `setTimeout(repaint, 900)`, which THREW THE ANSWER
 *     AWAY. Nine hundred milliseconds after a three-year-old's finger lands is
 *     roughly when they look up.
 *
 * A kid critic measured the screen 1.2 s after the tap, found `audio:cue` and
 * nothing else, and filed it as a sound-only reaction — which is LAW #1's
 * named failure mode. The measurement was late, but the verdict was right: an
 * answer that deletes itself is not an answer.
 *
 * So the answer is now DERIVED FROM STATE, not fired as an event. Whenever the
 * next thing the recipe wants is not in the bag, this card is on the screen —
 * before any tap, after any tap, and after any repaint. Tapping the tile makes
 * it SHOUT (a one-shot bounce keyed off `pulse`), and tapping the card itself
 * puts the kitchen away so the kid is standing in the room they need to walk out
 * of. Nothing here can fail, nothing is consumed and nothing is refused
 * (LAW #2); it is a signpost, drawn in the same hand as the food.
 */
function sourceCard(itemId: string, shout: boolean): HTMLElement {
  const card = el('div', 'mt-cook-source')
  card.setAttribute('role', 'button')
  card.setAttribute('aria-label', `Where to find ${getIngredient(itemId)?.devName ?? itemId}`)
  if (shout) card.dataset['shout'] = '1'
  card.append(foodEl(itemId, 58))
  card.append(svgEl(arrowBody(), 44, 'mt-ico mt-cook-srcarrow'))
  card.append(svgEl(sourceBody(sourcePicOf(itemId)), 84, 'mt-ico mt-cook-srcpic'))
  onTap(
    card,
    () => {
      // Something happens in the WORLD as well as on the sheet, so the answer is
      // visible even to a kid whose eyes are already off the panel.
      bus.emit('fx:play', { effect: 'sparkle', at: { ...app.playerPos }, count: 6 })
      close()
    },
    null,
  )
  return card
}

/** Can the kid put the next thing in right now? Pure; drives the card above. */
function shortOfNext(r: RecipeDef, stepIndex: number): string | null {
  const step = r.steps[stepIndex]
  if (step === undefined) return null
  const promised = state.filled.reduce((n, f, k) => n + (r.steps[k]?.item === step.item ? f : 0), 0)
  return inventory.count(step.item) <= promised ? step.item : null
}

// ---------------------------------------------------------------------------
// the three views
// ---------------------------------------------------------------------------

function buildPick(body: HTMLElement, _foot: HTMLElement): void {
  const scene = sceneEl('mt-cook-shelf')
  const grid = el('div', 'mt-grid mt-cook-grid')
  for (const r of allRecipes()) {
    const card = el('div', 'mt-card mt-cook-card')
    card.setAttribute('role', 'button')
    card.setAttribute('aria-label', r.devName)
    card.append(foodEl(r.dish, 104))
    const wants = el('div', 'mt-cook-wants')
    const need = recipeNeeds(r)
    let canMake = true
    for (const [item, n] of Object.entries(need)) {
      wants.append(haveRow(item, n))
      if (inventory.count(item) < n) canMake = false
    }
    card.append(wants)
    card.dataset['ready'] = String(canMake)
    if (collection.has(r.id)) {
      const known = el('span', 'mt-cook-known')
      known.append(iconEl('star', 30))
      card.append(known)
    }
    onTap(card, () => {
      state.recipe = r
      state.filled = r.steps.map(() => 0)
      state.stirs = 0
      state.view = 'cook'
      repaint()
    })
    grid.append(card)
  }
  scene.append(grid)
  body.append(scene)
  // NO BUTTON IN THE FOOT. The way out of the kitchen is the 112 px back button
  // `panelShell()` already put in the corner (§3) — this used to add a second
  // one, bottom-centre, doing exactly the same job.
}

/** Which step is being asked for right now, or -1 when the pot is full. */
function currentStep(r: RecipeDef): number {
  return state.filled.findIndex((f, i) => f < Math.max(1, Math.round(r.steps[i]?.count ?? 1)))
}

/**
 * THE POT. The biggest thing on the screen, and therefore the thing a
 * three-year-old taps first — so it is a control, it is saturated, and it
 * always answers (§1).
 *
 * Three stacked layers plus two garnishes, because the near rim has to paint
 * OVER the food for the food to look like it is IN the pot: back half, the
 * things a kid has actually tapped in, front half, then steam (any time there
 * is something to steam) and the spoon (the moment stirring is what to do).
 * No clip paths, no masks — five absolutely positioned SVGs.
 */
function potEl(r: RecipeDef, ready: boolean): HTMLElement {
  const inPot = state.filled.reduce((n, f) => n + f, 0)
  const counter = el('div', 'mt-cook-counter')

  // `mt-cook-stir` is added ONLY when stirring is the thing to do, so "is the
  // stir up yet?" is answerable from the DOM exactly as it was when the stir
  // was a separate button in the foot.
  const pot = el('div', ready ? 'mt-cook-pot mt-cook-stir' : 'mt-cook-pot')
  pot.setAttribute('role', 'button')
  pot.setAttribute('aria-label', ready ? 'Stir the pot' : 'The cooking pot')
  pot.dataset['ready'] = String(ready)
  pot.dataset['empty'] = String(inPot === 0)
  if (state.pulse !== null) pot.dataset['just'] = state.pulse

  pot.append(svgSpan('mt-cook-potback', potBackSvg()))
  // WHAT IS IN THERE IS DRAWN IN THERE (§2). One picture per thing tapped in,
  // the same picture that was tapped, so one fruit in never looks like none in.
  // Sized by how many there are so the mouth of the pot is always well filled
  // and the row never wraps — one fruit is BIG, four are smaller. The budget is
  // the mouth's width on the SMALLEST pot the game draws: the hand-me-down iPad
  // is 30vh of 768 = 230 px, less the 6% insets = 202 px. (Measured: at 64 px
  // the third fruit wrapped to a second row on that profile and sank.)
  const inside = el('span', 'mt-cook-potin')
  const shown = Math.min(inPot, 6)
  const pieceSize = shown <= 2 ? 76 : shown === 3 ? 60 : shown === 4 ? 50 : 40
  r.steps.forEach((s, i) => {
    const n = Math.min(state.filled[i] ?? 0, 6)
    for (let k = 0; k < n; k++) inside.append(foodEl(s.item, pieceSize))
  })
  pot.append(inside)
  pot.append(svgSpan('mt-cook-potfront', potFrontSvg()))
  if (inPot > 0) pot.append(svgSpan('mt-cook-steam', steamSvg()))
  if (ready) pot.append(svgSpan('mt-cook-spoon', spoonSvg()))

  onTap(pot, () => tapPot(), null)
  counter.append(pot)
  if (ready) {
    const left = el('div', 'mt-cook-stirdots')
    left.append(holes(Math.max(1, r.stirs ?? 3), state.stirs))
    counter.append(left)
  }
  return counter
}

/**
 * THE POT ALWAYS REACTS. When the pot is full it is the stir. Before that it
 * cannot silently do nothing — it wobbles and makes the ingredient that is
 * actually wanted jump on the counter, which is a picture answer to "what now?"
 * rather than a dead control (§1). Nothing is ever consumed or lost here.
 */
function tapPot(): void {
  const r = state.recipe
  if (r === null) return
  if (currentStep(r) === -1) {
    doStir()
    return
  }
  state.pulse = 'point'
  bus.emit('audio:cue', { cue: 'ui_tap' })
  repaint()
}

function buildCook(body: HTMLElement, foot: HTMLElement): void {
  const r = state.recipe
  if (r === null) {
    state.view = 'pick'
    buildPick(body, foot)
    return
  }
  const stepIndex = currentStep(r)
  const ready = stepIndex === -1
  const scene = sceneEl('mt-cook-kitchen')

  // -- the recipe card on the wall. THIS is the sequencing lesson ------------
  const order = el('div', 'mt-cook-order')
  const strip = el('div', 'mt-cook-strip')
  r.steps.forEach((s, i) => {
    const cell = el('div', 'mt-cook-step')
    cell.append(foodEl(s.item, 56))
    cell.append(holes(Math.max(1, Math.round(s.count)), state.filled[i] ?? 0))
    cell.dataset['current'] = String(i === stepIndex)
    cell.dataset['done'] = String((state.filled[i] ?? 0) >= Math.max(1, Math.round(s.count)))
    strip.append(cell)
  })
  order.append(strip)
  // A PAINTED ARROW, not the HUD's `back` chevron rotated 180 degrees at 70 %
  // opacity. That was a thin grey line glyph and the one piece of default-looking
  // UI on a screen that is otherwise all chunky filled shapes — the critic named
  // it. Same ink weight and same palette as the food beside it.
  const arrow = el('span', 'mt-cook-arrow')
  arrow.append(svgEl(arrowBody(), 40, 'mt-ico mt-cook-arrowpic'))
  order.append(arrow)
  // THE DISH, IN FULL COLOUR. A promise, not a disabled control.
  const goal = el('div', 'mt-cook-goal')
  goal.append(foodEl(r.dish, 92))
  order.append(goal)
  scene.append(order)

  // -- the pot, on the counter, and under it where to get the next thing -----
  // The card lives INSIDE the counter column rather than as a fourth thing in
  // the scene: the counter is the flexible row, so a card appearing shrinks the
  // pot instead of pushing the tray off the bottom of a 768 px iPad. It is also
  // the right place to read it — the pot is asking, and the answer is directly
  // under the question.
  const counter = potEl(r, ready)
  const missing = ready ? null : shortOfNext(r, stepIndex)
  if (missing !== null) {
    counter.dataset['hint'] = '1'
    counter.append(sourceCard(missing, state.pulse === 'source'))
  }
  scene.append(counter)

  // -- the bag, laid out on the counter in front of it -----------------------
  const tray = el('div', 'mt-cook-tray')
  const offered = new Set<string>([...r.steps.map((s) => s.item), ...inventory.ids()])
  const wanted = r.steps[stepIndex]?.item
  for (const item of [...offered].sort()) {
    if (getIngredient(item) === undefined && inventory.count(item) === 0) continue
    const have = inventory.count(item)
    const b = el('button', 'mt-btn big mt-cook-item')
    b.type = 'button'
    b.setAttribute('aria-label', getIngredient(item)?.devName ?? item)
    b.append(foodEl(item, 56))
    b.append(ticks(have))
    b.dataset['have'] = String(have)
    // NONE OF THESE IN THE BAG -> the tile wears a picture of where they come
    // from, permanently. This is the half of the fix a kid gets without tapping
    // anything at all: an empty-bag cook screen used to be one faded tile and a
    // back button, with no picture anywhere of where fruit grows.
    // 26 px, cornered. Measured bigger first: a 40 px stamp on a 96 px tile on
    // the hand-me-down iPad sat right across the fruit it was talking about.
    if (have === 0) b.append(sourceBadge(item, 26))
    if ((state.pulse === 'point' || state.pulse === 'giggle') && item === wanted) b.dataset['point'] = '1'
    // The tapped tile shakes. Set in the PAINT rather than by a timer on a node,
    // because the node the finger landed on does not survive the next repaint —
    // which is exactly how the old wobble came to be invisible.
    if ((state.pulse === 'giggle' || state.pulse === 'source') && item === state.pulseItem) {
      b.dataset['wobble'] = '1'
    }
    onTap(
      b,
      () => {
        addToPot(item)
      },
      null,
    )
    tray.append(b)
  }
  scene.append(tray)
  body.append(scene)
  // NOTHING IN THE FOOT (§3). Back to the shelf is the corner button, and the
  // stir is the pot itself.
}

function buildDone(body: HTMLElement, foot: HTMLElement): void {
  const r = state.made
  if (r === null) {
    state.view = 'pick'
    buildPick(body, foot)
    return
  }
  const scene = sceneEl('mt-cook-served')
  const card = el('div', 'mt-cook-done')
  card.append(foodEl(r.dish, 220))
  // HOW MANY OF THESE ARE NOW IN MY BAG — a quantity, so it wears ticks (§4).
  const bagRow = el('div', 'mt-cook-bag')
  bagRow.append(iconEl('bag', 44))
  bagRow.append(ticks(inventory.count(r.dish)))
  card.append(bagRow)
  scene.append(card)
  body.append(scene)

  foot.append(
    button({ icon: 'check', label: 'Keep it', className: 'big paper', iconSize: 60 }, () => {
      state.view = 'pick'
      state.made = null
      repaint()
    }),
  )
  foot.append(
    button({ icon: 'monster', label: 'Eat it', className: 'big primary', iconSize: 60, cue: null }, () => {
      eatDish(r)
    }),
  )
}

// ---------------------------------------------------------------------------
// doing things
// ---------------------------------------------------------------------------

/**
 * A WRONG INGREDIENT IS A GIGGLE, NOT A MISTAKE.
 *
 * Nothing is taken, nothing is spoiled, the recipe does not reset and the kid is
 * not told off. The button wobbles, the kitchen laughs, and the very next tap
 * can be the right one — which is the whole of brief §5 in one function.
 */
function addToPot(item: string): void {
  const r = state.recipe
  if (r === null) return
  const i = state.filled.findIndex((f, k) => f < Math.max(1, Math.round(r.steps[k]?.count ?? 1)))
  if (i === -1) return
  const step = r.steps[i]
  if (step === undefined) return

  if (step.item !== item) {
    // THE GIGGLE HAS TO BE SEEN, not only heard (brief §5). The tile that was
    // tapped wobbles, the one that IS wanted jumps, notes come off the monster,
    // and the kitchen laughs. Nothing is taken and nothing is spoiled.
    state.pulse = 'giggle'
    state.pulseItem = item
    bus.emit('audio:cue', { cue: 'giggle' })
    bus.emit('fx:play', { effect: 'notes', at: { ...app.playerPos }, count: 4 })
    repaint()
    return
  }
  // The right thing, but there are none left in the bag. That is "go and pick
  // some", and the picture of where they grow is ALREADY on the screen — the
  // repaint just makes it shout. Never a refusal, never a consumption.
  if (shortOfNext(r, i) !== null) {
    state.pulse = 'source'
    state.pulseItem = item
    bus.emit('audio:cue', { cue: 'nope_soft' })
    bus.emit('fx:play', { effect: 'poof', at: { ...app.playerPos }, count: 5 })
    repaint()
    return
  }

  state.filled[i] = (state.filled[i] ?? 0) + 1
  state.pulse = 'fill'
  bus.emit('audio:cue', { cue: 'pop_soft' })
  bus.emit('fx:play', { effect: 'sparkle', at: { ...app.playerPos }, count: 4 })
  repaint()
}

function doStir(): void {
  const r = state.recipe
  if (r === null) return
  const wanted = Math.max(1, r.stirs ?? 3)
  state.stirs++
  state.pulse = 'stir'
  bus.emit('audio:cue', { cue: state.stirs >= wanted ? 'ding_ready' : 'ladle' })
  bus.emit('monster:action', { action: 'cook', seconds: 0.9 })
  if (state.stirs < wanted) {
    repaint()
    return
  }
  finishDish(r)
}

/**
 * OUT COMES A DISH.
 *
 * The ingredients are taken here and nowhere else, so backing out half-way
 * through a recipe costs a kid nothing at all. `take()` is all-or-nothing; on
 * the impossible path where it says no we still hand over the dish, because a
 * kid who did everything right must never be left with neither.
 */
function finishDish(r: RecipeDef): void {
  for (const [item, n] of Object.entries(recipeNeeds(r))) inventory.take(item, n, 'cooking')
  inventory.add(r.dish, 1, 'cooking')

  const firstTime = !collection.has(r.id)
  collection.found(r.id)
  if (firstTime && (r.coins ?? 0) > 0) wallet.earnExact(r.coins ?? 0, 'cooking.newRecipe')

  bus.emit('kitchen:cooked', { recipeId: r.id, dishItemId: r.dish, count: 1 })
  bus.emit('monster:action', { action: 'cheer', seconds: 1.4 })
  bus.emit('fx:play', { effect: 'confetti', at: { ...app.playerPos }, count: 12 })
  bus.emit('audio:cue', { cue: 'chime_happy' })
  save.request('cooking:cooked')

  state.made = r
  state.recipe = null
  state.filled = []
  state.stirs = 0
  state.pulse = null
  state.view = 'done'
  repaint()
}

/** Breakfast. A gentle need ACCEPTED — never a meter refilled (LAW #2). */
function eatDish(r: RecipeDef): void {
  if (inventory.take(r.dish, 1, 'eaten')) {
    const out = needs.satisfy(r.feeds ?? 'hungry', `eat:${r.dish}`)
    if (out.bonusCoins > 0) wallet.earnExact(out.bonusCoins, 'cooking.breakfast')
    bus.emit('monster:action', { action: 'eat', seconds: 1.6 })
    bus.emit('fx:play', { effect: 'hearts', at: { ...app.playerPos }, count: 8 })
    bus.emit('audio:cue', { cue: 'yum' })
    save.request('cooking:ate')
  }
  state.view = 'pick'
  state.made = null
  repaint()
}

// ---------------------------------------------------------------------------
// the layer
// ---------------------------------------------------------------------------

function repaint(): void {
  if (layer === null) return
  clear(layer)
  const parts = panelShell({
    icon: state.view === 'done' ? 'dinner' : 'kitchen',
    onBack: () => {
      if (state.view === 'cook') {
        state.view = 'pick'
        state.recipe = null
        state.pulse = null
        repaint()
      } else close()
    },
  })
  parts.root.classList.add('mt-cook')
  parts.root.dataset['view'] = state.view
  if (state.view === 'pick') buildPick(parts.body, parts.foot)
  else if (state.view === 'cook') buildCook(parts.body, parts.foot)
  else buildDone(parts.body, parts.foot)
  layer.append(parts.root)
  // Consumed. Every animation keyed off it has already been created with the
  // attribute on, so it plays once and this cannot get stuck on (see `Pulse`).
  state.pulse = null
  state.pulseItem = null
}

/**
 * Emitting the same event we listen for would re-enter `openCookSheet()` for
 * ever, so the announcement is flagged and our own listener ignores it. Nobody
 * else has to know that — to the rest of the bus it is an ordinary
 * `ui:panel { panel: 'cook' }`, which is what closes the zoo sheet, closes the
 * collection book, and takes the HUD chrome off the glass (§6).
 */
let announcing = false
function announceOpen(): void {
  if (announcing) return
  announcing = true
  try {
    // TWICE, DELIBERATELY. The first tells everything else to put itself away —
    // and a sheet closing itself answers on the same bus with
    // `ui:panel { panel: null }`, which would leave the HUD believing no panel
    // is up and paint its chrome straight over ours (measured: opening the zoo
    // sheet and then the kitchen left `#hud[data-panel]` at '0'). The second
    // lands after they have all gone quiet, and is a no-op for every listener,
    // all of which are idempotent and none of which is open by then.
    bus.emit('ui:panel', { panel: COOK_PANEL })
    bus.emit('ui:panel', { panel: COOK_PANEL })
  } finally {
    announcing = false
  }
}

export function openCookSheet(recipeId?: string): void {
  if (layer === null) return
  const want = recipeId === undefined ? undefined : allRecipes().find((r) => r.id === recipeId)
  if (want !== undefined) {
    state = { ...EMPTY, view: 'cook', recipe: want, filled: want.steps.map(() => 0) }
  } else if (!cookSheetOpen()) {
    state = { ...EMPTY }
  }
  repaint()
  bus.emit('audio:cue', { cue: 'ui_open' })
  announceOpen()
}

export function close(): void {
  if (layer === null || !cookSheetOpen()) return
  clear(layer)
  state = { ...EMPTY }
  bus.emit('audio:cue', { cue: 'ui_close' })
  bus.emit('ui:panel', { panel: null })
}

/** Called once by `installCooking`. The layer is ours; the HUD's is untouched. */
export function installCookSheet(hud: HTMLElement): void {
  if (layer !== null) return
  layer = el('div', 'mt-cook-layer')
  hud.append(layer)

  // ONE panel at a time, across two independent layers. Anything else opening
  // closes the kitchen, and closing the kitchen says so — exactly the contract
  // `PanelHost` keeps inside `src/ui/**`, honoured from outside it.
  bus.on('ui:panel', (e) => {
    if (announcing) return // our own "I am open" — see `announceOpen()`
    if (e.panel === COOK_PANEL) openCookSheet()
    else if (e.panel !== null) close()
  })
  // Walking out of the kitchen puts the kitchen away.
  bus.on('scene:transitionStart', () => close())
}
